/*
 * Contest Leaderboard Upload Component
 *
 * This component is used to upload contest leaderboard data to the server.
 *
 * @version 1.0.0
 */
import React, { useState } from "react";
import Service from "../../Service/http";
import * as xlsx from "xlsx";
import { Link } from "react-router-dom";
import { FcDownload } from "react-icons/fc";
import { toast } from "react-toastify";
import { useQuery } from "react-query";

const ContestLeaderboardUpload = () => {
  // State for storing data from the uploaded file
  const [data, setData] = useState(null);

  // Service instance for API calls
  const service = new Service();

  // State for loading indicator
  const [loading, setLoading] = useState(false);

  // Function to handle contest leaderboard upload
  const handleContestLeaderboardUpload = async () => {
    // Check if a file is selected
    if (!data) {
      toast.error("No File is Chosen");
      return;
    }

    // Check the validity of the data
    if (!(await checkDataValidity(data))) {
      toast.error("Invalid data format");
      return;
    }

    // Perform the contest leaderboard upload
    setLoading(true)
    service
      .post("api/contest/leaderboard", { data: data })
      .then((json) => {
        setLoading(false);
        toast.success("Added FollowUp To Users");
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
        toast.error("Error occurred during upload");
      });
  };

  const {
    data: leaderboardProgress,
    isError: masterError,
    isLoading,
    refetch,
  } = useQuery(["leaderboardProgress"], () =>
    service
      .get("api/contest/leaderboard/progress/count")
      .then((response) => response)
  );

  const handleRefresh = () => {
    // Manually trigger the refetch when the refresh button is clicked
    refetch();
  };

  // Function to check the validity of the uploaded data
  const checkDataValidity = async (studentFeedbackList) => {
    const requiredProperties = [
      "contestId",
      "contestName",
      "contestSolvedCount",
      "division",
      "siteName",
      "userName",
      "rank",
      "score",
    ];

    // Helper functions for stricter validation
    const isCodechefValid = (contestId, contestName) => {
      return (
        /^START\d+$/.test(contestId) && 
        /^Starters\s.+$/.test(contestName) 
      );
    };
  
    const isLeetcodeValid = (contestId, contestName) => {
      return (
        (/^weekly-contest-\d+$/.test(contestId) || /^biweekly-contest-\d+$/.test(contestId)) &&
        (/^Weekly Contest \d+$/.test(contestName) || /^Biweekly Contest \d+$/.test(contestName))
      );
    };
  
    const isCodeforcesValid = (contestId, contestName) => {
      const contestIdAsNumber = Number(contestId); 
      const isValidNumber = !isNaN(contestIdAsNumber) && Number.isInteger(contestIdAsNumber);
      const isNameValid = contestName.length > String(contestId).length;
      const isValid = isValidNumber && isNameValid;
      return isValid;
    };

    for (const user of studentFeedbackList) {
      if (!requiredProperties.every((prop) => user.hasOwnProperty(prop))) {
        return false;
      }
  
      const { siteName, contestId, contestName } = user;
  
      if (!["CODECHEF", "LEETCODE", "CODEFORCES", "MENTORPICK"].includes(siteName)) {
        return false;
      }
  
      switch (siteName) {
        case "CODECHEF":
          if (!isCodechefValid(contestId, contestName)) {
            return false;
          }
          break;
  
        case "LEETCODE":
          if (!isLeetcodeValid(contestId, contestName)) {
            return false;
          }
          break;
  
        case "CODEFORCES":
          if (!isCodeforcesValid(contestId, contestName)) {
            return false;
          }
          break;
  
        case "MENTORPICK":
         
          break;
  
        default:
          return false;
      }
    }
    return true;
  };

  // Function to handle file upload
  const handleFileUpload = (e) => {
    e.preventDefault();

    // Check if files are present
    if (e.target.files) {
      const reader = new FileReader();

      // Read the file content
      reader.onload = (e) => {
        const data = e.target.result;
        const workbook = xlsx.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = xlsx.utils.sheet_to_json(worksheet);
        setData(json);
      };

      // Read the file as an array buffer
      reader.readAsArrayBuffer(e.target.files[0]);
    }
  };

  return (
    <div className="card">
      <div
        className="card-title text-primary"
        style={{ textAlign: "center", margin: "2%" }}
      >
        <h3>Contest Leaderboard Update</h3>
        <div>
          {masterError ? (
            <p>Error loading leaderboard progress data</p>
          ) : (
            <div>
              {isLoading ? (
                <p>Loading...</p>
              ) : (
                <div>
                  <h4>{leaderboardProgress?.count}</h4>
                  {leaderboardProgress?.count ? (
                    <p>
                      Still {leaderboardProgress.count} record(s) in progress.
                    </p>
                  ) : (
                    <p>All records updated.</p>
                  )}
                </div>
              )}
            </div>
          )}

          <button className="btn btn-primary" onClick={handleRefresh}>
            Refresh
          </button>
        </div>
      </div>
      <div className="card-body">
      <div>
  <p className="card-text" style={{ margin: "2%" }}>
    <b>Leaderboard Upload Instructions</b>
    <br />
    To ensure a successful leaderboard upload, please follow these guidelines:  
    <ul>
      <li><b>Use the Provided Template</b>: Download and refer to <i>Contest-leaderboard-upload-sample.xlsx</i>.</li>
      <li><b>Do Not Modify Column Names:</b> Keep the headers exactly as they are.</li>
      <li><b>Complete All Fields:</b> Ensure no field is left blank.</li>
      <li><b>Follow the Format:</b> The sample file includes records for each site; replicate the exact structure.</li>
    </ul>
    
  </p>

  <p className="card-text" style={{ margin: "2%" }}>
    <b>Site-Specific Instructions</b>
    <ul>
      <li><b>CodeChef:</b> Please format the contestID as <b>"START***"</b>.</li>
      <li><b>Codeforces:</b> Use the specific contestID provided by Codeforces, which is generally a four-digit number in the URL (e.g., contest/<b>1837</b>). Do not use the round number.</li>
      <li><b>LeetCode:</b> contestID should follow the format <b>"biweekly-contest-***"</b> or <b>"weekly-contest-***"</b> and keep division as 1.</li>
      <li><b>MentorPick:</b> Copy the contestID directly from the contest URL using the <b>cslug</b> attribute (e.g., mentorpick.com/contest/........<b>cslug=bz-snist-y23-p1-in-class-contest-1</b>……..) and and keep division as 1</li>
    </ul>
    <p><b>Note:</b> Please ensure you copy the exact name of the contest directly from the platform; do not attempt to type it yourself.</p>
  </p>
  <Link
      to="/files/Contest-leaderboard-upload-sampleV2.xlsx"
      target="_blank"
      download
      style={{ marginLeft: "8px", display: "inline-flex", alignItems: "center" }}
    >
      <FcDownload style={{ marginRight: "4px" }} /> <i>Download Sample</i>
    </Link>
    <p style={{ color: "red", fontWeight: "bold" }}>
      Any deviation from these instructions will result in upload failure.
    </p>
</div>


        <br />
        {/* File Input */}
        <div className="my-2">
          <input
            className="form-control"
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileUpload}
          />
        </div>
        {/* Upload Button */}
        <div>
          <button
            disabled={loading}
            onClick={handleContestLeaderboardUpload}
            className="btn btn-success m-2 mx-2 float-end"
          >
            Upload Data
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContestLeaderboardUpload;
